import React from 'react';
import {   InfoBox,   ExerciseBox,   CodeBlock,   InlineCode,   SectionTitle, Result, Note,  TableContainer,  Highlight,  CorrectionBox,  SectionHeading,  SubHeading,  TwoColGrid,  ThreeColGrid,  GridItem,  Divider, Separator} from '../../../../components/course/CourseComponents';

const Chapitre1Content = ({ courseId = 'monpremierjeu' }) => {
  return (
    <>
      <InfoBox title="Objectif du chapitre" color="indigo">
        <p className="mb-0">
          Créer une boucle de jeu, et mettre en place le squelette de notre jeu.
        </p>
      </InfoBox>      

      <Separator type="text" text="Comment fonctionne un jeu vidéo ?" color="gray-darker" size="md" />
      <SectionTitle>🧩 Comment fonctionne un jeu vidéo ?</SectionTitle>
      
      <p>Un jeu vidéo, c'est un programme qui :</p>
      
      <ul className="list-disc list-inside mb-4">
        <li>Crée une fenêtre (l'écran du jeu)</li>
        <li>Affiche des objets (comme un joueur, un ennemi, un décor…), ou simplement un cercle dans notre cas</li>
        <li>Écoute ce que fait le joueur (est-ce qu'il appuie sur une touche du clavier, est-ce qu'il bouge la souris…)</li>
        <li>Réagit à ses actions (par exemple : « tu as cliqué au bon endroit »)</li>
        <li>Met à jour ce qu'on voit à chaque instant, environ 60 fois par seconde, ce qu'on appelle les FPS (frames per second)</li>
      </ul>

      <Separator type="text" text="Le framework p5.js" color="gray-darker" size="md" />
      <SectionTitle>📗 Le framework p5.js</SectionTitle>

      <p>
        Pour coder un jeu, il faut un moteur ou simplement une bibliothèque qui nous offre des fonctions pour :
      </p>
      
      <ul className="list-disc list-inside mb-4">
        <li>l'affichage</li>
        <li>la gestion des entrées (clavier, souris)</li>
        <li>le chargement d'images, de sons…</li>
      </ul>

      <p>
        Pour cette formation, nous allons utiliser <Highlight>p5.js</Highlight>, une bibliothèque de création de jeux vidéo en JavaScript.
        <br />
        p5.js met à ta disposition plein de fonctions pour t'aider à créer ton jeu.
      </p>

      <Separator type="text" text="Les fonctions principales" color="gray-darker" size="md" />
      <SectionTitle>Les fonctions principales de p5.js</SectionTitle>

      <SubHeading>🏁 Fonctions appelées au lancement du jeu</SubHeading>

      <Note type="gray" title="setup()">
        <ul className="list-disc list-inside mb-2">
          <li>Appelée une seule fois au début</li>
          <li>Sert à préparer le jeu (créer la fenêtre, initialiser les variables…)</li>
        </ul>
      </Note>

      <SubHeading>🔁 Fonctions appelées à chaque frame (en boucle)</SubHeading>

      <Note type="gray" title="draw()">
        <ul className="list-disc list-inside mb-2">
          <li>Appelée environ 60 fois par seconde (60 FPS : Frame Per Second)</li>
          <li>Sert à mettre à jour et dessiner l'écran</li>
        </ul>
      </Note>

      <SubHeading>🖱️ Fonctions appelées lors des interactions</SubHeading>

      <Note type="gray" title="mousePressed()">
        <p>Appelée chaque fois qu'on clique avec la souris.</p>
      </Note>

      <Separator type="text" text="La boucle de jeu" color="gray-darker" size="md" />
      <Note type="exercice" title="🧪 Crée la boucle de jeu :">
       Dans l'editeur de code, crée la boucle de jeu en ajoutant les fonctions :<br/>
       - setup()<br/>
       - draw()<br/>
       - mousePressed()<br/>
          <CodeBlock language="javascript" showCopyButton={true}>
{`// Jeu 1 : Améliore ta visée
function setup() {

}

function draw() {

}

function mousePressed() {

}
`}
          </CodeBlock>
   
      </Note>



      <Separator type="text" text="Fonctions d'affichage" color="gray-darker" size="md" />
      
      <Note type="tip" title="Création de la fenêtre de jeu et du fond">
      On va maintenant : <br/>- créer la fenêtre de jeu lors de l'initialisation dans la fonction setup().<br/>- colorer le fond à chaque frame dans la fonction draw().
      </Note>


      <TableContainer>
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800">
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Fonction</th>
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>createCanvas(600, 600)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Crée la fenêtre de jeu. <br/>- Paramètre 1 : largeur du canvas en pixel<br/> - Paramètre 2 : hauteur du canvas en pixel</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>background(rouge, vert, bleu)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Colore le fond de la fenêtre. <br/>- Paramètre 1 : Composante Rouge de 0 a 255<br/> - Paramètre 2 : Composante Verte de 0 a 255<br/> - Paramètre 3 : Composante Bleue de 0 a 255</td>
            </tr>
          </tbody>
        </table>
      </TableContainer>

      <Note type="exercice" title="🧪 Crée la fenêtre de jeu et colore le fond :">
       - Dans la fonction setup(), ajoute la fonction createCanvas() avec les parametres 600 et 600<br/>
       <CodeBlock language="javascript">
{`createCanvas(600, 600);`}
          </CodeBlock>
       - Dans la fonction draw(), ajoute la fonction background() avec les parametres 9, 26, 52 (Bleu foncé) <br/>
          <CodeBlock language="javascript">
{`background(9, 26, 52);
`}
          </CodeBlock>
          - Puis clique sur le bouton Play ▶️ pour l'exécuter.

          <br/><br/>
          <Result>Tu devrais voir un fond bleu foncé.</Result>

          <CorrectionBox>
        <CodeBlock language="javascript" showCopyButton={true}>
{`function setup() {
  createCanvas(600, 600);
}

function draw() {
  background(9, 26, 52);
}`}
        </CodeBlock>
      </CorrectionBox>

      </Note>

      <Note type="info" title="🎯 À essayer :">
        Change la couleur du fond en changeant les paramètres de la fonction background() avec n'importe quelle valeur entre 0 et 255.<br/>
        Si tu veux du rouge, tu peux mettre 255 pour le rouge, 0 pour le vert et 0 pour le bleu.<br/>
        Si tu veux du vert, tu peux mettre 0 pour le rouge, 255 pour le vert et 0 pour le bleu.<br/>
        Si tu veux du bleu, tu peux mettre 0 pour le rouge, 0 pour le vert et 255 pour le bleu.<br/>
        Tu peux aussi mettre 0 pour toutes les composantes pour avoir du noir, ou 255 pour toutes les composantes pour avoir du blanc.<br/>
        Ou alors mixer chaque composante RVB pour avoir des couleurs intermédiaires.<br/><br/>
        
        <TableContainer>
          <table className="w-full border-collapse border border-gray-300 dark:border-gray-600 text-sm">
            <tbody>
              <tr>
                <td className="border border-gray-300 dark:border-gray-600 p-2 text-s">Gris clair</td>
                <td className="border border-gray-300 dark:border-gray-600 p-2 text-s"><InlineCode>220, 220, 220</InlineCode></td>
                <td className="border border-gray-300 dark:border-gray-600 p-2 text-s">Rouge</td>
                <td className="border border-gray-300 dark:border-gray-600 p-2 text-s"><InlineCode>255, 0, 0</InlineCode></td>
              </tr>
              <tr>
                <td className="border border-gray-300 dark:border-gray-600 p-2 text-s">Vert</td>
                <td className="border border-gray-300 dark:border-gray-600 p-2 text-s"><InlineCode>0, 255, 0</InlineCode></td>
                <td className="border border-gray-300 dark:border-gray-600 p-2 text-s">Bleu</td>
                <td className="border border-gray-300 dark:border-gray-600 p-2 text-s"><InlineCode>0, 0, 255</InlineCode></td>
              </tr>
              <tr>
                <td className="border border-gray-300 dark:border-gray-600 p-2 text-s">Jaune</td>
                <td className="border border-gray-300 dark:border-gray-600 p-2 text-s"><InlineCode>255, 255, 0</InlineCode></td>
                <td className="border border-gray-300 dark:border-gray-600 p-2 text-s">Magenta</td>
                <td className="border border-gray-300 dark:border-gray-600 p-2 text-s"><InlineCode>255, 0, 255</InlineCode></td>
              </tr>
              <tr>
                <td className="border border-gray-300 dark:border-gray-600 p-2 text-s">Cyan</td>
                <td className="border border-gray-300 dark:border-gray-600 p-2 text-s"><InlineCode>0, 255, 255</InlineCode></td>
                <td className="border border-gray-300 dark:border-gray-600 p-2 text-s">Blanc</td>
                <td className="border border-gray-300 dark:border-gray-600 p-2 text-s"><InlineCode>255, 255, 255</InlineCode></td>
              </tr>
              <tr>
                <td className="border border-gray-300 dark:border-gray-600 p-2 text-s">Noir</td>
                <td className="border border-gray-300 dark:border-gray-600 p-2 text-s"><InlineCode>0, 0, 0</InlineCode></td>
                <td className="border border-gray-300 dark:border-gray-600 p-2 text-s">Gris</td>
                <td className="border border-gray-300 dark:border-gray-600 p-2 text-s"><InlineCode>128, 128, 128</InlineCode></td>
              </tr>
              <tr>
                <td className="border border-gray-300 dark:border-gray-600 p-2 text-s">Marron</td>
                <td className="border border-gray-300 dark:border-gray-600 p-2 text-s"><InlineCode>128, 0, 0</InlineCode></td>
                <td className="border border-gray-300 dark:border-gray-600 p-2 text-s">Olive</td>
                <td className="border border-gray-300 dark:border-gray-600 p-2 text-s"><InlineCode>128, 128, 0</InlineCode></td>
              </tr>
            </tbody>
          </table>
        </TableContainer>
        <br/>
        <p>Tu peux aussi utiliser le <a href="https://htmlcolorcodes.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 font-medium hover:text-blue-800 dark:hover:text-blue-300 underline">Color Picker RGB</a> disponible dans "Menu &gt; Ressources et outils" pour choisir une couleur.</p>
      </Note>

      <Separator type="text" text="Detection du clic de la souris" color="gray-darker" size="md" />
      
      <Note type="tip" title="Clic de la souris">
      Enfin, on va afficher un message dans la console lors d'un clic de souris.
      </Note>

      <Note type="exercice" title="🧪 Affiche un message dans la console lors d'un clic de souris :">
      - Dans la fonction mousePressed(), ajoute un message du genre <InlineCode>print("Clic !");</InlineCode>
      <br/><br/>
      <Result>▶️ Clique et regarde la console. Tu devrais voir le message "Clic !" apparaître.</Result>

      <CorrectionBox>
        <CodeBlock language="javascript" showCopyButton={true}>
{`function mousePressed() {
  print("Clic !");
}`}
        </CodeBlock>
      </CorrectionBox>
      </Note>

      <Note type="info" title="🎯 À essayer :">
        Clique sur la fenêtre de jeu et regarde la console. Tu devrais voir le message "Clic !" apparaître.
      </Note>

      <Separator type="text" text="Correction" color="gray-darker" size="md" />

      <Note type="tip" title="✅ Correction">
      Si tout s'est bien passé, tu devrais avoir un code du genre :
      </Note>

      <CodeBlock language="javascript" showCopyButton={true}>
{`function setup() {
  createCanvas(600, 600);
}

function draw() {
  background(9, 26, 52);
}

function mousePressed() {
  print("Clic !");
}
`}
      </CodeBlock>

      <Note type="info" title="💡 Prêt pour la suite ?">
        Maintenant que tu as créé la boucle de jeu, nous allons pouvoir commencer à créer le gameplay du jeu!
      </Note>

    </>
  );
};

export default Chapitre1Content; 